'use client'

import { APOLLO_CLIENT_SIDE_OPTIONS } from '@/lib/apollo/clients/apolloClient.client'
import { useIsAuthenticated } from '@/lib/auth/client/AuthProvider.client'
import { isSSR } from '@/lib/next/services/nextjs.rendering'
import { useQuery } from '@apollo/client'
import { gql } from '@gql-generated/gql'
import { ProductListFavoriteStatusDocument } from '@gql-generated/graphql'
import { useMemo } from 'react'

gql(/* GraphQL */ `
  query productListFavoriteStatus($references: [String!]!) {
    productsByReferences(references: $references) {
      reference
      isFavorite
    }
  }
`)

type UseProductListFavoriteStatusReturnType = {
  loading: boolean
  data: Record<string, boolean> | undefined
}

export function useProductsListFavoriteStatus(
  references: string[],
): UseProductListFavoriteStatusReturnType {
  const isAuthenticated = useIsAuthenticated()
  const { data, loading } = useQuery(ProductListFavoriteStatusDocument, {
    ...APOLLO_CLIENT_SIDE_OPTIONS,
    variables: {
      references,
    },
    skip: references.length === 0 || !isAuthenticated || isSSR,
  })

  const productsByReferences = data?.productsByReferences

  const isLoadingByProductReference = useMemo<UseProductListFavoriteStatusReturnType>(() => {
    if (loading) {
      return { loading: true, data: undefined }
    }

    if (!productsByReferences) {
      return { loading: false, data: undefined }
    }

    const map: Record<string, boolean> = Object.fromEntries(
      productsByReferences?.map((product) => [product.reference, product.isFavorite]),
    )

    return { loading: false, data: map }
  }, [productsByReferences, loading])

  return isLoadingByProductReference
}
